import React, { useEffect, useState } from "react";
import axios from "axios";
import { Mail, Download, Loader2, CheckCircle2, Link2, ListChecks, Sparkles, Shield } from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

export default function GuideQuotidiane() {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [downloadingPillole, setDownloadingPillole] = useState(false);

  const scaricaPillole = async () => {
    setDownloadingPillole(true);
    try {
      const r = await axios.get(`${API}/api/download/pillole-tutela-digitale`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "tutelarsi_online_in_10_pillole_acdt.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (_e) {
      alert("Download non disponibile, riprova.");
    } finally {
      setDownloadingPillole(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/api/guide-quotidiane`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData(false));
  }, []);

  const scarica = async (giorno) => {
    setDownloading(giorno);
    try {
      const r = await axios.get(`${API}/api/guide-quotidiane/${giorno}/pdf`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `guida_giorno_${giorno}_acdt.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setData((d) => d && { ...d, guide: d.guide.map((g) => (g.giorno === giorno ? { ...g, scaricata: true } : g)) });
    } catch (_e) {
      alert("Download non disponibile, riprova.");
    } finally {
      setDownloading(null);
    }
  };

  if (data === null) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-[#102a6b]" /></div>;
  if (data === false) return <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700 text-sm">Errore di caricamento delle guide.</div>;

  const oggi = data.guide.find((g) => g.giorno === data.giorno_corrente);

  return (
    <div className="space-y-6" data-testid="guide-quotidiane">
      <div className="rounded-2xl bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] text-white p-6 md:p-8 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 h-44 w-44 rounded-full bg-[#f59e0b]/25 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f59e0b] text-[#0a1f55] text-[11px] font-extrabold uppercase tracking-wider">
            <Mail className="h-3.5 w-3.5" /> Le guide delle email quotidiane
          </div>
          <h2 className="mt-3 text-xl md:text-2xl font-extrabold">Guide Business per il tuo lavoro</h2>
          <p className="mt-2 text-slate-200 text-[13.5px] max-w-2xl">
            Ogni giorno ricevi un'email formativa: qui trovi la <strong>guida PDF approfondita</strong> di ogni argomento,
            con checklist operative e fonti esterne autorevoli. Scaricale e usale nel tuo business e con i tuoi clienti.
          </p>
        </div>
      </div>

      {/* PDF Tutelarsi online in 10 pillole */}
      <div className="rounded-2xl border-2 border-[#102a6b]/25 bg-white p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid="pillole-banner">
        <div className="h-12 w-12 shrink-0 rounded-xl bg-[#102a6b]/10 text-[#102a6b] flex items-center justify-center"><Shield className="h-6 w-6" /></div>
        <div className="flex-1">
          <div className="text-[10.5px] uppercase tracking-wider font-extrabold text-[#102a6b]">🛡️ Guida esclusiva soci</div>
          <h3 className="font-extrabold text-[#0a1f55]">Tutelarsi online in 10 pillole</h3>
          <p className="text-[12.5px] text-slate-600 mt-0.5">Il vademecum A.C.D.T. della tutela digitale, personalizzato con il tuo nome e numero tessera: 10 regole pratiche per difendersi online, da usare anche con i tuoi clienti.</p>
        </div>
        <Button onClick={scaricaPillole} disabled={downloadingPillole} data-testid="pillole-download-btn" className="bg-[#102a6b] hover:bg-[#0a1f55] text-white font-bold">
          {downloadingPillole ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="mr-2 h-4 w-4" /> Scarica il PDF</>}
        </Button>
      </div>

      {oggi && (
        <div className="rounded-2xl border-2 border-[#f59e0b]/60 bg-gradient-to-r from-amber-50 via-white to-amber-50 p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid="guida-oggi-banner">
          <div className="h-12 w-12 shrink-0 rounded-xl bg-[#f59e0b]/20 text-[#b45309] flex items-center justify-center"><Sparkles className="h-6 w-6" /></div>
          <div className="flex-1">
            <div className="text-[10.5px] uppercase tracking-wider font-extrabold text-[#b45309]">📩 L'email di oggi · Giorno {oggi.giorno} · {oggi.categoria}</div>
            <h3 className="font-extrabold text-[#0a1f55]">{oggi.titolo}</h3>
            <p className="text-[12.5px] text-slate-600 mt-0.5">{oggi.sintesi}</p>
          </div>
          <Button onClick={() => scarica(oggi.giorno)} disabled={downloading === oggi.giorno} data-testid="guida-oggi-download" className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold">
            {downloading === oggi.giorno ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="mr-2 h-4 w-4" /> Scarica la guida di oggi</>}
          </Button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.guide.map((g) => (
          <div key={g.giorno} data-testid={`guida-card-${g.giorno}`} className={`rounded-2xl border bg-white p-5 flex flex-col transition-all hover:shadow-md ${g.giorno === data.giorno_corrente ? "border-[#f59e0b]/60" : "border-slate-200 hover:border-[#102a6b]/30"}`}>
            <div className="flex items-center justify-between">
              <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-[#102a6b]/8 bg-slate-100 text-[#102a6b] font-extrabold uppercase tracking-wider">Giorno {g.giorno} · {g.categoria}</span>
              {g.scaricata && <CheckCircle2 className="h-4 w-4 text-emerald-600" title="Già scaricata" />}
            </div>
            <h3 className="mt-2.5 font-bold text-[#0a1f55] leading-snug text-[14.5px]">{g.titolo}</h3>
            <p className="mt-1.5 text-[12.5px] text-slate-600 leading-relaxed flex-1">{g.sintesi}</p>
            <div className="mt-3 flex items-center gap-4 text-[11px] text-slate-500 font-semibold">
              <span className="inline-flex items-center gap-1"><ListChecks className="h-3.5 w-3.5" /> {g.checklist_count} azioni</span>
              <span className="inline-flex items-center gap-1"><Link2 className="h-3.5 w-3.5" /> {g.fonti_count} fonti</span>
            </div>
            <Button
              onClick={() => scarica(g.giorno)}
              disabled={downloading === g.giorno}
              data-testid={`guida-download-${g.giorno}`}
              variant="outline"
              className="mt-4 w-full border-slate-300 text-[#102a6b] font-bold hover:bg-[#102a6b] hover:text-white"
            >
              {downloading === g.giorno ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="mr-2 h-4 w-4" /> Scarica PDF</>}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
